import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX, Maximize, Minimize, Loader2, AlertTriangle, Settings, Captions, SkipForward } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { api, fmtTime } from "@/lib/api";
import { usePlayback } from "@/hooks/usePlayback";
import { useTracks } from "@/hooks/useTracks";
import { PlayerMenu, SubtitleOverlay } from "@/components/PlayerMenu";

const MODE_LABEL = { direct: "Original file", hls: "Plex transcode", remux: "Live convert" };

const Btn = ({ onClick, label, testid, children }) => (
  <button onClick={onClick} title={label} aria-label={label} data-testid={testid}
    className="flex h-9 w-9 items-center justify-center rounded-full text-white/90 transition-[background-color,transform] hover:bg-white/15 active:scale-95">{children}</button>
);


export const VideoPlayer = ({ video, startAt = 0, onEnded }) => {
  const pb = usePlayback(video.id, startAt);
  const { ref, base, cur: info, offset, error, load, fallback } = pb;
  const t = useTracks(video, pb);
  const [menu, setMenu] = useState(false);
  const wrap = useRef(null);
  const [state, setState] = useState({ t: startAt, playing: false, waiting: true, vol: 1, muted: false });
  const [scrub, setScrub] = useState(null);
  const [ui, setUi] = useState(true);
  const [full, setFull] = useState(false);
  const last = useRef({ t: 0, d: 0 });
  const idle = useRef(null);

  const el = () => ref.current;
  const now = () => offset + (el()?.currentTime || 0);
  const duration = info?.mode === "remux" || !isFinite(el()?.duration) || !el()?.duration ? base?.duration || 0 : el().duration;

  const save = useCallback(() => {
    const { t, d } = last.current;
    if (t > 5 && d > 0) api.post(`/videos/${video.id}/progress`, { position: t, duration: d }).catch(() => {});
  }, [video.id]);
  useEffect(() => { const i = setInterval(() => { if (!el()?.paused) save(); }, 10000); return () => { clearInterval(i); save(); }; }, [save]); // eslint-disable-line react-hooks/exhaustive-deps

  const seek = (target) => {
    const tt = Math.max(0, Math.min(target, duration || target));
    if (info?.mode === "remux") load(info, tt); else el().currentTime = tt;
    setState((s) => ({ ...s, t: tt }));
  };
  const pickMode = (m) => {
    if (m === "direct") { t.resetForDirect(); load(base.direct, now()); } else load(base.convert, now());
  };
  const toggle = () => (el().paused ? el().play().catch(() => {}) : el().pause());
  const toggleFull = () => (document.fullscreenElement ? document.exitFullscreen() : wrap.current.requestFullscreen?.());
  const setVol = (v) => { el().volume = v; el().muted = v === 0; };
  const poke = () => { setUi(true); clearTimeout(idle.current); idle.current = setTimeout(() => setUi(false), 2800); };

  useEffect(() => {
    const onFs = () => setFull(!!document.fullscreenElement);
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName) || !el()) return;
      const k = e.key.toLowerCase();
      if (k === " " || k === "k") { e.preventDefault(); toggle(); } else if (k === "arrowright") seek(now() + 10);
      else if (k === "arrowleft") seek(now() - 10); else if (k === "f") toggleFull(); else if (k === "m") el().muted = !el().muted;
      else if (k === "c" && t.tracks?.subtitles.length) t.pickSub(t.sub ? null : t.tracks.subtitles.find((s) => s.kind === "text") || t.tracks.subtitles[0]);
      else if (k === "escape") setMenu(false);
      else return;
      poke();
    };
    document.addEventListener("fullscreenchange", onFs);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("fullscreenchange", onFs); window.removeEventListener("keydown", onKey); };
  });

  const sync = () => {
    const v = el();
    const t = now();
    last.current = { t, d: duration };
    setState({ t, playing: !v.paused, waiting: v.readyState < 3 && !v.paused, vol: v.volume, muted: v.muted });
  };
  const ended = () => { last.current = { t: duration, d: duration }; save(); onEnded?.(); };
  const shown = scrub ?? state.t;
  const marker = (video.markers || []).find((m) => state.t >= m.start && state.t < m.end - 1);

  return (
    <div ref={wrap} onMouseMove={poke} onClick={poke} data-testid="video-player"
      className={`group relative overflow-hidden bg-black ${full ? "h-screen w-screen" : "aspect-video w-full rounded-xl border border-zinc-800 shadow-2xl"} ${ui || !state.playing || menu ? "" : "cursor-none"}`}>
      <video ref={ref} className="h-full w-full bg-black" playsInline onClick={toggle} onTimeUpdate={sync} onPlay={sync} onPause={() => { sync(); save(); }}
        onWaiting={sync} onPlaying={sync} onVolumeChange={sync} onLoadedMetadata={sync} onEnded={ended} onError={fallback} data-testid="video-element" />
      {marker && !error && (
        <button onClick={(e) => { e.stopPropagation(); seek(marker.end); }} data-testid={`player-skip-${marker.type}`}
          className="absolute bottom-28 right-6 z-10 flex h-11 items-center gap-2 rounded-full border border-white/25 bg-black/60 px-5 text-sm font-medium text-white backdrop-blur-md transition-[background-color,transform] animate-rise hover:bg-white hover:text-black active:scale-95">
          <SkipForward size={15} />{marker.type === "intro" ? "Skip intro" : "Skip credits"}
        </button>
      )}
      <SubtitleOverlay cues={t.cues} time={state.t} raised={ui || !state.playing || menu} />
      {menu && <PlayerMenu base={base} mode={info?.mode} t={t} onMode={pickMode} onAudio={t.pickAudio} onSub={t.pickSub} />}
      {(state.waiting || !info) && !error && <Loader2 size={40} className="pointer-events-none absolute left-1/2 top-1/2 -ml-5 -mt-5 animate-spin text-white/80" data-testid="player-loading" />}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 px-8 text-center text-white" data-testid="player-error">
          <AlertTriangle className="text-amber-400" /><p className="max-w-md text-sm">{error}</p>
        </div>
      )}
      <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-4 pb-3 pt-16 transition-opacity duration-300 sm:px-6 ${ui || !state.playing || menu ? "opacity-100" : "opacity-0"}`}>
        <Slider value={[Math.min(shown, duration || 0)]} max={duration || 1} step={1} onValueChange={([v]) => setScrub(v)}
          onValueCommit={([v]) => { setScrub(null); seek(v); }} className="cursor-pointer [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-amber-400 [&>span:first-child]:h-1 [&>span:first-child]:bg-white/25 [&>span:first-child>span]:bg-amber-400" data-testid="player-progress-bar" />
        <div className="mt-2 flex items-center gap-1 text-white">
          <Btn onClick={toggle} label={state.playing ? "Pause" : "Play"} testid="player-play-pause-button">{state.playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}</Btn>
          <Btn onClick={() => seek(now() - 10)} label="Back 10 seconds" testid="player-back-button"><RotateCcw size={16} /></Btn>
          <Btn onClick={() => seek(now() + 10)} label="Forward 10 seconds" testid="player-forward-button"><RotateCw size={16} /></Btn>
          <Btn onClick={() => { el().muted = !el().muted; }} label="Mute" testid="player-mute-button">{state.muted || state.vol === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}</Btn>
          <Slider value={[state.muted ? 0 : state.vol]} max={1} step={0.05} onValueChange={([v]) => setVol(v)} className="hidden w-24 sm:flex [&>span:first-child]:h-1 [&>span:first-child]:bg-white/25 [&>span:first-child>span]:bg-white [&_[role=slider]]:h-3 [&_[role=slider]]:w-3" data-testid="player-volume-slider" />
          <span className="ml-3 font-mono text-xs text-white/80" data-testid="player-time">{fmtTime(shown)} / {fmtTime(duration)}</span>
          <span className="flex-1" />
          {info && <span className="mr-2 hidden rounded-full border border-white/20 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/70 sm:inline" data-testid="player-mode">{MODE_LABEL[info.mode]}</span>}
          {t.sub && <Captions size={16} className="mr-1 text-amber-300" data-testid="player-sub-active" />}
          <Btn onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }} label="Audio, subtitles & playback" testid="player-settings-button"><Settings size={16} className={`transition-transform duration-300 ${menu ? "rotate-90" : ""}`} /></Btn>
          <Btn onClick={toggleFull} label="Fullscreen" testid="player-fullscreen-button">{full ? <Minimize size={16} /> : <Maximize size={16} />}</Btn>
        </div>
      </div>
    </div>
  );
};
